// SP-3 — the TPS setup checklist, top of the TPS detail Overview tab. The
// Monthly Cycle Cockpit pattern (MonthlyCycleTab): a stepper strip with
// server-derived states + ONE current-step card that explains, in plain
// language, what the step is, why it matters, and what happens next — with
// exactly one permission-gated action. Steps whose baton is external
// (Stripe verifying the firm, the firm's contact setting a password) get a
// waiting sentence naming the firm, never a button-shaped dead end. A
// first-time admin should be able to onboard a TPS start-to-finish from
// this card alone.

import { useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  Skeleton,
  SubmitButton,
  useToast,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  useCertifyTps,
  useEmailTpsOnboardingLink,
  useInviteTpsPortal,
  useProvisionTpsConnect,
  useRefreshTpsConnectStatus,
  useTpsSetup,
} from "@/hooks/portfolioAdmin/useTps";
import type {
  TpsConnectOnboardingResultDto,
  TpsDto,
  TpsSetupDto,
  TpsSetupStepDto,
} from "@/types/tps";
import { SETUP_STEP_LABEL, TpsSetupStepper } from "./TpsSetupStepper";

interface TpsSetupChecklistProps {
  tps: TpsDto;
  /** Switches the detail page to the Fee Schedule tab (`?tab=fees`). */
  onGoToFees: () => void;
}

/** The one step the card narrates: the first thing Phoenix can act on,
 *  else the first external baton (waiting). */
function currentStep(setup: TpsSetupDto): TpsSetupStepDto | null {
  return (
    setup.steps.find((s) => s.state === "active") ??
    setup.steps.find((s) => s.state === "waiting") ??
    null
  );
}

function formatMonthlyFee(setup: TpsSetupDto): string {
  if (setup.monthlyFeeAmount == null) return "—";
  if (setup.monthlyFeeAmountType === "Percent") {
    return `${(setup.monthlyFeeAmount * 100).toLocaleString("en-US", {
      maximumFractionDigits: 4,
    })}% p.a.`;
  }
  return formatCurrency(setup.monthlyFeeAmount, { maximumFractionDigits: 2 });
}

function accountBadgeStatus(
  status: string | null,
): "finalized" | "review" | "error" | "archived" {
  switch (status) {
    case "Active":
      return "finalized";
    case "Pending":
      return "review";
    case "Restricted":
    case "Disabled":
      return "error";
    default:
      return "archived";
  }
}

/** Plain-language label for a Stripe Connect account status — the raw Stripe
 *  vocabulary ("Restricted"/"Pending") means nothing to a servicer firm. */
function accountStatusLabel(status: string | null): string {
  switch (status) {
    case "Active":
      return "Ready";
    case "Restricted":
      return "Verifying with Stripe";
    case "Pending":
      return "Not started";
    case "Disabled":
      return "Action needed";
    default:
      return "Not created";
  }
}

function PermissionNote({ permission }: { permission: string }) {
  return (
    <p
      className="text-small text-gray-mid m-0 mt-space-3"
      data-testid="setup-step-permission-note"
    >
      Someone with the {permission} permission takes it from here.
    </p>
  );
}

function AccountStatusBadges({ setup }: { setup: TpsSetupDto }) {
  return (
    <div
      className="flex flex-wrap items-center gap-space-2 mt-space-3"
      data-testid="connect-account-badges"
    >
      <Badge status={accountBadgeStatus(setup.clearingAccountStatus)}>
        Clearing: {accountStatusLabel(setup.clearingAccountStatus)}
      </Badge>
      <Badge status={accountBadgeStatus(setup.commissionsAccountStatus)}>
        Commissions: {accountStatusLabel(setup.commissionsAccountStatus)}
      </Badge>
    </div>
  );
}

function OnboardingLinks({
  result,
  firmName,
}: {
  result: TpsConnectOnboardingResultDto;
  firmName: string;
}) {
  const accounts = [
    { label: "Clearing", account: result.clearing },
    { label: "Commissions", account: result.commissions },
  ].filter((a) => a.account.status !== "Active" && a.account.onboardingUrl);
  if (accounts.length === 0) return null;
  return (
    <div
      className="mt-space-3 rounded-lg border border-gold bg-gold/10 p-space-3"
      data-testid="connect-onboarding-links"
    >
      <p className="text-small text-onyx m-0 mb-space-2">
        Send this secure Stripe link to {firmName} — they complete identity
        and bank verification there.
      </p>
      <div className="flex flex-col gap-space-1">
        {accounts.map(({ label, account }) => (
          <a
            key={account.purpose}
            href={account.onboardingUrl!}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-space-1 text-small text-gold-shadow hover:underline"
            data-testid={`connect-onboarding-link-${label.toLowerCase()}`}
          >
            <ExternalLink size={14} aria-hidden />
            Open {label} account onboarding link
          </a>
        ))}
      </div>
    </div>
  );
}

/** One-line facts for already-completed steps, so the card reads as a
 *  record of what's on file — not just "what's next". */
function CompletedStepFacts({ setup }: { setup: TpsSetupDto }) {
  // Above the early return below — hooks cannot sit behind one.
  const { tz } = useDateFormatter();
  const done = new Set(
    setup.steps.filter((s) => s.state === "done").map((s) => s.key),
  );
  if (done.size === 0) return null;
  return (
    <ul
      className="list-none p-0 m-0 mt-space-4 flex flex-col gap-space-1 border-t border-border-light pt-space-3"
      data-testid="setup-completed-facts"
    >
      {done.has("details") && (
        <li className="text-small text-gray-mid">
          Entity and contact email on file
          {setup.contactEmail ? ` (${setup.contactEmail})` : ""}.
        </li>
      )}
      {done.has("certified") && (
        <li className="text-small text-gray-mid">
          Certified
          {setup.certifiedAt ? ` ${formatDateInTz(setup.certifiedAt, tz)}` : ""}.
        </li>
      )}
      {done.has("connect") && (
        <li className="text-small text-gray-mid">
          Stripe onboarding complete — Clearing and Commissions accounts are
          active.
        </li>
      )}
      {/* F6 — status text only; account management lives on the page's
          Portal account tab, never a cross-link off the entity. */}
      {done.has("portal-user") && (
        <li className="text-small text-gray-mid">
          Portal access active
          {setup.portalUserEmail ? ` for ${setup.portalUserEmail}` : ""}.
        </li>
      )}
      {done.has("fee-schedule") && (
        <li className="text-small text-gray-mid">
          Monthly fee: {formatMonthlyFee(setup)}
        </li>
      )}
    </ul>
  );
}

export function TpsSetupChecklist({ tps, onGoToFees }: TpsSetupChecklistProps) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const setupQuery = useTpsSetup(tps.id);
  const setup = setupQuery.data;

  const certify = useCertifyTps(tps.id);
  const invite = useInviteTpsPortal(tps.id);
  const provision = useProvisionTpsConnect(tps.id);
  const refreshStatus = useRefreshTpsConnectStatus(tps.id);
  const emailLink = useEmailTpsOnboardingLink(tps.id);

  const [certifyConfirmOpen, setCertifyConfirmOpen] = useState(false);
  const [provisionResult, setProvisionResult] =
    useState<TpsConnectOnboardingResultDto | null>(null);

  // The default placeholder is "no servicer assigned" — the detail page
  // renders an explanatory banner instead of a checklist.
  if (tps.isDefault) return null;

  if (setupQuery.isLoading) {
    return (
      <Card data-testid="tps-setup-loading">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-5 w-full mt-space-3" />
      </Card>
    );
  }

  if (setupQuery.isError || !setup) {
    return (
      <Card data-testid="tps-setup-error">
        <p className="text-small text-status-error m-0 mb-space-2">
          Couldn&apos;t load this TPS&apos;s setup status. Refresh to try
          again.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void setupQuery.refetch()}
        >
          Retry
        </Button>
      </Card>
    );
  }

  const firmName = setup.tpsName?.trim() ? setup.tpsName : "the firm";
  const step = currentStep(setup);

  const canCertify = hasPermission("Portfolio.CertifyTps");
  const canManageStripe = hasPermission("Portfolio.ManageTpsStripe");
  const canManageTps = hasPermission("Tps.Manage");

  const runCertify = () => {
    certify
      .mutateAsync(null)
      .then(() => {
        setCertifyConfirmOpen(false);
        toast.success(`${firmName} is certified.`);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const runInvite = (resend: boolean) => {
    invite
      .mutateAsync()
      .then((account) =>
        toast.success(
          resend
            ? `Invitation re-sent to ${account.email}.`
            : `Invitation sent to ${account.email}.`,
        ),
      )
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const runProvision = () => {
    provision
      .mutateAsync()
      .then((result) => setProvisionResult(result))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const runRefreshStatus = () => {
    refreshStatus
      .mutateAsync()
      .then(() => toast.info("Stripe status refreshed."))
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const runEmailLink = () => {
    emailLink
      .mutateAsync()
      .then((result) =>
        toast.success(`Onboarding link emailed to ${result.sentToEmail}.`),
      )
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Card data-testid="tps-setup-checklist">
      <div className="flex flex-wrap items-center justify-between gap-space-3 mb-space-4">
        <h3 className="font-display font-bold text-h3 m-0">Setup</h3>
        {setup.isReady && <Badge status="finalized">Ready</Badge>}
      </div>
      <TpsSetupStepper steps={setup.steps} />

      {setup.isReady ? (
        <div
          className="mt-space-4 rounded-lg border border-status-success/30 bg-status-success/10 p-space-3"
          data-testid="setup-ready-banner"
        >
          <p className="text-small text-onyx m-0">
            This TPS is ready — loans can now be assigned to it from a
            loan&apos;s Overview tab.
          </p>
        </div>
      ) : step ? (
        <div className="mt-space-4" data-testid="setup-current-step">
          <p className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-2">
            {SETUP_STEP_LABEL[step.key]}
          </p>

          {step.key === "details" && (
            <>
              <p className="text-body text-onyx m-0">
                This TPS needs a name (its own, or a linked entity) and a
                contact email — the email is where the portal invitation goes.
                Edit these from this Overview tab.
              </p>
              {step.detail === "missing-contact-email" && (
                <p className="text-small text-gray-mid m-0 mt-space-2">
                  Add a contact person with an email, or set the
                  entity&apos;s email.
                </p>
              )}
            </>
          )}

          {step.key === "certified" && (
            <>
              <p className="text-body text-onyx m-0">
                Certification records that Phoenix has an executed servicing
                agreement with this firm. Once certified, its Stripe payment
                accounts can be set up.
              </p>
              {canCertify ? (
                <div className="mt-space-3">
                  <Button
                    onClick={() => setCertifyConfirmOpen(true)}
                    data-testid="setup-certify-button"
                  >
                    Certify this TPS
                  </Button>
                </div>
              ) : (
                <p
                  className="text-small text-gray-mid m-0 mt-space-3"
                  data-testid="setup-certify-admin-note"
                >
                  An administrator certifies this TPS once the servicing
                  agreement is executed.
                </p>
              )}
            </>
          )}

          {step.key === "connect" && step.state === "active" && (
            <>
              <p className="text-body text-onyx m-0">
                Two Stripe accounts are created for the firm: Clearing
                (receives borrower payments) and Commissions (receives the
                firm&apos;s fees). Money never sits with Phoenix.
              </p>
              <p className="text-small text-gray-mid m-0 mt-space-2">
                The firm completes identity and bank verification on Stripe —
                they&apos;ll need their legal business name, EIN, business
                address, and bank routing / account numbers. Email them the
                link and they can also generate a fresh one any time from their
                portal&apos;s Payment setup tab.
              </p>
              <AccountStatusBadges setup={setup} />
              {canManageStripe ? (
                <div className="mt-space-3 flex flex-wrap gap-space-2">
                  <SubmitButton
                    type="button"
                    pending={provision.isPending}
                    pendingLabel="Setting up…"
                    onClick={runProvision}
                    data-testid="setup-provision-button"
                  >
                    Set up Stripe accounts
                  </SubmitButton>
                  <SubmitButton
                    type="button"
                    variant="secondary"
                    pending={emailLink.isPending}
                    pendingLabel="Emailing…"
                    onClick={runEmailLink}
                    data-testid="setup-email-link-button"
                  >
                    Email link to {firmName}
                  </SubmitButton>
                </div>
              ) : (
                <PermissionNote permission="Portfolio.ManageTpsStripe" />
              )}
            </>
          )}

          {step.key === "connect" && step.state === "waiting" && (
            <>
              <p className="text-body text-onyx m-0" data-testid="setup-connect-waiting">
                Waiting on {firmName} — Stripe is verifying their details
                (usually 1–2 business days). Nothing is needed from Phoenix
                right now.
              </p>
              <AccountStatusBadges setup={setup} />
              {canManageStripe ? (
                <div className="mt-space-3 flex flex-wrap gap-space-2">
                  <SubmitButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    pending={refreshStatus.isPending}
                    pendingLabel="Checking…"
                    onClick={runRefreshStatus}
                    data-testid="setup-refresh-status-button"
                  >
                    Refresh status
                  </SubmitButton>
                  <SubmitButton
                    type="button"
                    variant="text"
                    size="sm"
                    pending={provision.isPending}
                    pendingLabel="Fetching…"
                    onClick={runProvision}
                    data-testid="setup-reshow-links-button"
                  >
                    Get onboarding links again
                  </SubmitButton>
                  <SubmitButton
                    type="button"
                    variant="text"
                    size="sm"
                    pending={emailLink.isPending}
                    pendingLabel="Emailing…"
                    onClick={runEmailLink}
                    data-testid="setup-email-link-button"
                  >
                    Email link to {firmName}
                  </SubmitButton>
                </div>
              ) : (
                <PermissionNote permission="Portfolio.ManageTpsStripe" />
              )}
            </>
          )}

          {step.key === "connect" && provisionResult && (
            <OnboardingLinks result={provisionResult} firmName={firmName} />
          )}

          {step.key === "portal-user" && step.state === "active" && (
            <>
              <p className="text-body text-onyx m-0">
                Invite {setup.contactEmail ?? "the firm's contact"} to the
                TPS portal — the firm reviews and approves bills and payouts
                there.
              </p>
              {canManageTps ? (
                <div className="mt-space-3">
                  <SubmitButton
                    type="button"
                    pending={invite.isPending}
                    pendingLabel="Sending…"
                    onClick={() => runInvite(false)}
                    data-testid="setup-invite-button"
                  >
                    Send portal invite
                  </SubmitButton>
                </div>
              ) : (
                <PermissionNote permission="Tps.Manage" />
              )}
            </>
          )}

          {step.key === "portal-user" && step.state === "waiting" && (
            <>
              <p
                className="text-body text-onyx m-0"
                data-testid="setup-portal-waiting"
              >
                Invitation sent to {setup.portalUserEmail ?? "the firm's contact"}{" "}
                — waiting on them to set a password.
              </p>
              {canManageTps ? (
                <div className="mt-space-3">
                  <SubmitButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    pending={invite.isPending}
                    pendingLabel="Re-sending…"
                    onClick={() => runInvite(true)}
                    data-testid="setup-resend-invite-button"
                  >
                    Re-send invite
                  </SubmitButton>
                </div>
              ) : (
                <PermissionNote permission="Tps.Manage" />
              )}
            </>
          )}

          {step.key === "fee-schedule" && (
            <>
              <p className="text-body text-onyx m-0">
                Set the firm&apos;s monthly servicing fee — it&apos;s copied
                onto each loan when the loan is assigned to this TPS.
              </p>
              <div className="mt-space-3">
                <Button onClick={onGoToFees} data-testid="setup-fees-button">
                  Set fee schedule
                </Button>
              </div>
            </>
          )}

          {step.key === "ready" && (
            <p className="text-body text-onyx m-0">
              Complete the steps above; loan assignment is blocked until this
              TPS is ready.
            </p>
          )}
        </div>
      ) : (
        <p className="text-body text-onyx m-0 mt-space-4">
          Complete the steps above; loan assignment is blocked until this TPS
          is ready.
        </p>
      )}

      <CompletedStepFacts setup={setup} />

      <p
        className="text-caption text-gray-mid m-0 mt-space-3"
        data-testid="setup-dcl-note"
      >
        Before this firm can bill a loan, its debt-collector disclosure for
        the borrower&apos;s state must be approved in the TPS portal —
        that&apos;s checked per loan at statement time.
      </p>

      <Modal
        open={certifyConfirmOpen}
        onClose={() => setCertifyConfirmOpen(false)}
        title="Certify this TPS"
        description={`Certifying records that Phoenix has an executed servicing agreement with ${firmName}.`}
        size="sm"
        footer={
          <div className="flex justify-end gap-space-2">
            <Button
              variant="text"
              size="sm"
              onClick={() => setCertifyConfirmOpen(false)}
            >
              Cancel
            </Button>
            <SubmitButton
              type="button"
              size="sm"
              pending={certify.isPending}
              pendingLabel="Certifying…"
              onClick={runCertify}
              data-testid="setup-certify-confirm"
            >
              Certify
            </SubmitButton>
          </div>
        }
      >
        <p className="text-small text-onyx m-0">
          This is a contractual attestation — only certify once the servicing
          agreement is signed. Certification unlocks Stripe account setup and,
          ultimately, loan assignment.
        </p>
      </Modal>
    </Card>
  );
}
